<template>
  <div>
    <div class="px-2">
      <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
        <div class="col-12 col-sm-6 col-md-4 col-lg-4">
          <label for="colFormLabel" class="col-form-label">Client: </label>
          <AsyncSelect
            placeholder="Client Name, ID, Mobile No"
            :api-service="fetchContactProfiles"
            :reduce="(name) => name.id"
            v-model="contactProfileId"
            :additional-query="clientQuery"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="d-flex justify-content-end px-2 mt-1">
      <button @click="exportReport" class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1">
        Export
      </button>
      <button @click="goToPrint" class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect me-1">
        Print
      </button>
    </div>

    <div class="card card-height mt-2">
      <div class="table-container">
        <table class="table create-billing table-width table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Reference No.</th>
              <th>Client Details</th>
              <th class="text-right">SubTotal Amount</th>
              <th class="text-right">VAT Amount</th>
              <th class="text-right">Discount</th>
              <th class="text-right">Redeem Point</th>
              <th class="text-right">Bill Amount</th>
              <th class="text-right">Paid Amount</th>
              <th class="text-right" v-for="mop in mopHeads" :key="mop.key">{{ mop.label }}</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(item, index) in tableItems"
              :key="index"
              :class="{
                'sale-has-return': item.has_return,
                'is-return': item.is_return
              }"
            >
              <td>{{ formatToDdMmmYy(item.date) }}</td>
              <td>{{ item.type }}</td>
              <td>
                {{ item.bill_number }}
                <span v-if="item.return_ref" class="small">
                  [Ref # {{ item.return_ref }}]
                </span>
              </td>
              <td>{{ item.client_details }}</td>
              <td class="text-right">{{ commaFormat(item.subtotal_amount) }}</td>
              <td class="text-right">{{ commaFormat(item.vat_amount) }}</td>
              <td class="text-right">{{ commaFormat(item.discount_amount) }}</td>
              <td class="text-right">{{ commaFormat(item.redeem_point) }}</td>
              <td class="text-right">{{ commaFormat(item.bill_amount) }}</td>
              <td class="text-right">{{ commaFormat(item.paid_amount) }}</td>
              <td class="text-right" v-for="mop in mopHeads" :key="mop.key">
                {{ commaFormat((item.mop_heads && item.mop_heads[mop.key]) || 0) }}
              </td>
            </tr>
          </tbody>

          <tfoot>
            <tr>
              <td colspan="4"><strong>Total</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.subtotal_amount) }}</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.vat_amount) }}</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.discount_amount) }}</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.redeem_point) }}</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.bill_amount) }}</strong></td>
              <td class="text-right"><strong>{{ commaFormat(totals.paid_amount) }}</strong></td>
              <td class="text-right" v-for="mop in mopHeads" :key="mop.key">
                <strong>{{ commaFormat((totals.mop_heads && totals.mop_heads[mop.key]) || 0) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
        <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
      </div>
    </div>
    <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination" />
    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import Pagination from '@/components/atom/Pagination';
import Loader from '@/components/atom/LoaderComponent.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import useDate from '@/services/utils/day';
import figureFormatter from '@/services/utils/figureFormatter';
import handleInventory from '@/services/modules/inventory';
import handleCompany from '@/services/modules/company';
import handleBarcode from '@/services/modules/barcode';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';
import pdfPrinter from '@/services/utils/pdf/salesCollectionReportPdf';

const router = useRouter();
const route  = useRoute();
const store  = useStore();
const companyInfo = ref({});

const tableItems = ref([]);
const totals     = ref({});
const mopHeads   = ref([]);
const offset     = ref(20);
const loading    = ref(false);
const contactProfileId = ref(null);
const clientQuery = { roles: 'customer' };

const { fetchSalesCollectionReport } = handleInventory();
const { fetchCompanyInfo }           = handleCompany();
const { fetchBarcode }               = handleBarcode();
const { exportToPDF }                = pdfPrinter();
const { fetchContactProfiles }       = handleContact();
const { formatPatientLabel }         = useAsyncDropdownHelper();
const { formatToDdMmmYy } = useDate();
const { commaFormat }     = figureFormatter();
const showError = inject('showError');

const companyId = computed(() => route.params.companyId);
const startDate = computed(() => route.query.start);
const endDate   = computed(() => route.query.end);
const page      = computed(() => route.query.page);

const appendClientQuery = (query) => {
  if (!contactProfileId.value) return query;
  return query + '&contact_profile_id=' + contactProfileId.value;
};

const buildQuery = () => {
  let query = '?company_id=' + companyId.value;
  if (startDate.value) query += '&start_date=' + startDate.value;
  if (endDate.value)   query += '&end_date='   + endDate.value;
  query += '&offset=' + offset.value;
  query += '&page='   + page.value;
  query += '&group_by=invoice';
  return appendClientQuery(query);
};

const loadData = async () => {
  loading.value    = true;
  tableItems.value = [];
  totals.value     = {};
  mopHeads.value   = [];

  await fetchSalesCollectionReport(buildQuery())
    .then((res) => {
      if (!res.status) {
        store.commit('resetPagination');
        return;
      }
      tableItems.value = res.data.rows;
      totals.value     = res.data.totals;
      mopHeads.value   = res.data.mop_heads;
      store.commit('setPaginationData', {
        records: res.data.total,
        perPage: res.data.per_page,
        currentPage: res.data.current_page,
      });
    })
    .catch(() => showError('Failed to fetch report'))
    .finally(() => loading.value = false);
};

const onClickGo = async () => {
  let routeQuery = Object.assign({}, route.query);
  delete routeQuery.page;
  delete routeQuery.contact_profile_id;
  if (contactProfileId.value) routeQuery.contact_profile_id = contactProfileId.value;
  await router.push({ path: route.path, query: routeQuery });
  store.commit('setPaginationData', { records: 0, perPage: offset.value, currentPage: 1 });
  await loadData();
};

const onPageChange = async (p) => {
  let routeQuery = Object.assign({}, route.query);
  routeQuery.page = p;
  await router.push({ path: route.path, query: routeQuery });
  await loadData();
};

const goToPrint = async () => {
  const userName = store.state.user.user.name;
  loading.value  = true;
  try {
    let query = '?company_id=' + companyId.value + '&is_all=1&group_by=invoice';
    if (startDate.value) query += '&start_date=' + startDate.value;
    if (endDate.value)   query += '&end_date='   + endDate.value;
    const res = await fetchSalesCollectionReport(appendClientQuery(query));
    if (!res.status) return;

    const qrcodeData = JSON.stringify({
      created_by: userName,
      created_date_time: new Date().toISOString()
    });
    const barcodeRes = await fetchBarcode(`?code=sales-collection-report&qrcode=${qrcodeData}`);
    exportToPDF(companyInfo.value, res.data, startDate.value, endDate.value, barcodeRes.qrcode, 'invoice');
  } catch (e) {
    showError('Failed to generate PDF');
  } finally {
    loading.value = false;
  }
};

const exportReport = () => {
  const token = localStorage.getItem('token');
  let query = `?company_id=${companyId.value}&is_all=1&is_export=1&group_by=invoice`;
  if (startDate.value) query += `&start_date=${startDate.value}`;
  if (endDate.value)   query += `&end_date=${endDate.value}`;
  query = appendClientQuery(query);
  query += `&_token=${token}`;
  const url = `${process.env.VUE_APP_BASE_URL}/export/reports/sales-collection-report${query}`;
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
};

onMounted(() => {
  if (route.query.contact_profile_id) contactProfileId.value = Number(route.query.contact_profile_id);
  loadData();
  fetchCompanyInfo('?company_id=' + companyId.value).then((res) => {
    if (res.status) companyInfo.value = res.data;
  });
});
</script>

<style scoped>
.card-height {
  min-height: 200px;
}
.sale-has-return {
  background-color: #fff8e1;
}
.is-return td{
  color: red;
}
</style>
